import { useEffect, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';

const ADMIN_GROUP = 'group:default/backstage_devops';

type TeamOption = { value: string; label: string };

const isWriterGroup = (ref: string) => {
  const normalized = ref.trim().toLowerCase();
  return (
    normalized.startsWith('group:default/') &&
    !normalized.endsWith('_reader') &&
    normalized !== 'group:default/external_clients'
  );
};

export const TechneTeamOwnerFieldSchema = makeFieldSchema({
  output: z => z.string(),
  uiOptions: z => z.object({}),
});

export function TechneTeamOwnerField(
  props: FieldExtensionComponentProps<string>,
) {
  const {
    onChange,
    schema,
    rawErrors,
    required,
    formData,
    idSchema,
  } = props;
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [items, setItems] = useState<TeamOption[]>([]);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const identity = await identityApi.getBackstageIdentity();
        const owned = (identity.ownershipEntityRefs ?? []).filter(isWriterGroup);
        const isAdmin = owned.some(
          ref => ref.trim().toLowerCase() === ADMIN_GROUP,
        );

        let options: TeamOption[] = owned.map(ref => ({
          value: ref,
          label: ref.replace(/^group:default\//, ''),
        }));

        if (isAdmin) {
          const result = await catalogApi.getEntities({
            filter: { kind: 'Group' },
            fields: ['metadata.name', 'spec.profile.displayName'],
          });
          const fromCatalog = result.items
            .map(entity => {
              const name = entity.metadata.name;
              const ref = `group:default/${name}`;
              if (!isWriterGroup(ref)) {
                return null;
              }
              const display = (entity.spec as { profile?: { displayName?: string } })
                ?.profile?.displayName;
              return {
                value: ref,
                label: display?.trim() || name,
              } satisfies TeamOption;
            })
            .filter((item): item is TeamOption => Boolean(item));
          if (fromCatalog.length > 0) {
            options = fromCatalog;
          }
        }

        const unique = Array.from(
          new Map(options.map(item => [item.value.toLowerCase(), item])).values(),
        );
        if (cancelled) return;
        setItems(unique);
        setLocked(!isAdmin && unique.length === 1);

        const current = String(formData ?? '').trim();
        const stillValid = unique.some(
          item => item.value.toLowerCase() === current.toLowerCase(),
        );
        if (!stillValid && unique[0]) {
          onChange(unique[0].value);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(
            caught instanceof Error
              ? caught.message
              : 'Não foi possível listar os grupos do catálogo.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Só na montagem: o owner segue o time logado / catálogo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = schema.title ?? 'Owner (grupo do time)';
  const labelId = `${idSchema?.$id ?? 'techne-owner'}-label`;

  return (
    <FormControl
      margin="normal"
      required={required}
      error={(rawErrors?.length ?? 0) > 0 || Boolean(error)}
      fullWidth
      disabled={loading || locked}
    >
      <InputLabel id={labelId} shrink>
        {title}
        {loading ? '…' : ''}
      </InputLabel>
      <Select
        labelId={labelId}
        label={title}
        value={formData ?? ''}
        displayEmpty
        onChange={event => onChange(String(event.target.value))}
        IconComponent={
          loading
            ? () => (
                <Box mr={1.5} display="flex" alignItems="center">
                  <CircularProgress size={16} />
                </Box>
              )
            : undefined
        }
      >
        {items.map(item => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {error ??
          (locked
            ? 'Componente entra no catálogo do seu time.'
            : 'Quem cria associa o componente a este grupo (sem _reader).')}
      </FormHelperText>
    </FormControl>
  );
}
